// Cleanup Classes
import { useKaraoke } from '@src/hooks/useKaraoke';
import {
  useEditor,
  useKaraokeStore,
  useTranscription,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { analytics } from '@src/utility/analytics';
import { forIPad } from '@src/utility/styled';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { useTranscriptOperations } from '../../hooks/useTranscriptOperations';
import ReplaceIcon from '../../svgs/ReplaceIcon';
import { SidebarOption } from '../../types.ts/general';
import { TranscriptionStatus } from '../../types.ts/video';
import { inDebugMode } from '../../utility/debug';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import { RepairIcon, TranscriptHistoryIcon } from '../common/icons';
import Modal from '../common/Modal';
import PhotoModalTop from '../common/PhotoModalTop';
import PhotoModal from '../common/PhotosModal';
import DebugModal from '../debug/DebugModal';
import { TranscriptionDiff as TranscriptionDiffNew } from './_TranscriptionDiff';
import CopyTranscriptIconButton from './CopyTranscriptIconButton';
import EditTimingSection from './EditTimingSection';
import { KaraokeBreaksSubtitlesView } from './KaraokeBreaksSubtitlesView';
import { KaraokeBreaksView } from './KaraokeBreaksView';
import { KaraokeLinesView } from './KaraokeLinesView';
import { RegenerateTranscriptionModal } from './RegenerateTranscriptionModal';
import { TranscriptContextMenu } from './TranscriptContextMenu';
import { TranscriptionText } from './TranscriptionText';
import { getTranscriptElementBlocks } from './utils';
import { WordReplacementModal } from './WordReplacementModal';

import type {
  ImageKey,
  ImageWithType,
  TranscriptContextMenuActions,
} from '../../types.ts/general';
import type { TranscriptElementBlock } from './utils';
import type { KaraokeLine } from '@src/editor/types/karaoke';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type { MouseEvent } from 'react';

export const Transcript: React.FC = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const transcriptionStore = useTranscription();
  const karaokeStore = useKaraokeStore();
  const editorStore = useEditor();

  const { enableFixTranscriptButton } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const useNewKaraoke = editorStore.useNewKaraoke;

  const [cursor, setCursor] = useState<{ from: number; to: number }>({
    from: 0,
    to: 0,
  });
  const [lastTimeUpdated, setLastTimeUpdated] = useState<number>(0);
  const [autoScroll, setAutoScroll] = useState<boolean>(true);
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [contextMenuActions, setContextMenuActions] = useState<
    TranscriptContextMenuActions[] | null
  >(null);
  const [showReplaceModal, setShowReplaceModal] = useState<Record<
    'startIndex' | 'endIndex',
    number
  > | null>(null);
  const [showEditTimingModal, setShowEditTimingModal] = useState<{
    startIndex: number;
    endIndex: number;
  } | null>(null);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [showReplaceWarning, setShowReplaceWarning] = useState<boolean>(false);
  const [photoSelection, togglePhotoSelectionModal] = useState<{
    loc: Record<'startIndex' | 'endIndex', number>;
    text: string;
  } | null>(null);
  const [selectedImage, setSelectedImage] = useState<
    ImageWithType[ImageKey] | null
  >(null);

  // Create adapter and hook for business logic
  // Use videoCreator directly - no adapter needed
  const showSubtitles = videoCreator.getShowSubtitles();

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const originalTranscription = transcriptionStore.getOriginalTranscription();
  const hasKaraokeProduced = useNewKaraoke
    ? karaokeStore.hasKaraokeLines
    : videoCreator.karaokeProducer.hasElements();
  const isInKaraokeView = showSubtitles && hasKaraokeProduced;
  const karaokeLanguage = useNewKaraoke
    ? karaokeStore.karaokeLanguage
    : videoCreator.karaokeProducer.getKaraokeConfig().language;
  const isSubtitlesTranslated =
    isInKaraokeView &&
    !originalTranscription?.language.includes('en') &&
    karaokeLanguage === 'english';
  const subtitleElements = videoCreator.subtitleElements;

  const karaokeLines = useNewKaraoke
    ? karaokeStore.getKaraokeLinesFormatted()
    : undefined;

  const transcriptElements = useMemo(() => {
    if (isInKaraokeView && isSubtitlesTranslated) {
      return subtitleElements;
    }
    if (isInKaraokeView && karaokeLines && useNewKaraoke) {
      // mark hidden tokens as muted as useTranscriptOperations expects it
      return karaokeLines.flatMap(line =>
        line.transcriptTokens.map(token => ({
          ...token,
          state: line.allHiddenTranscriptTokenIds.includes(token.id)
            ? 'muted'
            : token.state,
        })),
      );
    }
    return transcriptionStore.getFinalTranscriptionElements();
  }, [
    karaokeLines,
    isInKaraokeView,
    showHistory,
    isSubtitlesTranslated,
    subtitleElements,
    transcriptionStore.getFinalTranscriptionElements(),
  ]);

  const transcriptBlocks = useMemo(() => {
    if (!showHistory) return [];
    return computed(() =>
      getTranscriptElementBlocks(
        transcriptionStore.getFinalTranscriptionElements(),
        transcriptionStore.getOriginalTranscription()?.elements || [],
      ),
    ).get();
  }, [showHistory, transcriptionStore.getFinalTranscriptionElements()]);

  // Initialize business logic hook
  const {
    selectedWordIndex,
    setSelectedWordIndex,
    getBoundaryIndexesFromSelection,
    handleCursorMove,
    handleAddPunctuation,
    handleAddKaraokeBreak,
    handleTranscriptRestore,
    getSelectedStartEndIndex,
    handleHideKaraoke,
    handleTranscriptDelete,
    getCursorPosition,
    skipToWordWithIndex,
    getActions,
    handleRemovePhoto,
    handleAddPhoto,
    handleCutText,
    handlePasteText,
    handleSaveAsClip,
    handleTranscriptTextReplace,
  } = useTranscriptOperations(
    videoCreator,
    transcriptElements,
    isSubtitlesTranslated,
    showHistory,
    transcriptBlocks,
    karaokeLines,
  );

  const { commandProduceKaraoke } = useKaraoke(editorStore.editor);
  const { isKaraokeGenerated } = useKaraokeStore();

  const [debugEnabled, setDebugEnabled] = useState(false);

  // autoscroll transcript when playing
  useEffect(() => {
    setAutoScroll(videoCreator.isPlaying);
  }, [videoCreator.isPlaying]);

  // recalculate cursor position when switching views (indexes change)
  useEffect(() => {
    if (useNewKaraoke) {
      setCursor(getCursorPosition());
    }
  }, [isInKaraokeView]);

  useEffect(() => {
    if (!autoScroll) return;
    requestAnimationFrame(() => {
      scrollRef?.current
        ?.querySelector(
          `[data-index='${Math.min(
            cursor.to + (cursor.to < 10 ? 1 : 10),
            (transcriptElements?.length || 0) - 1,
          )}']`,
        )
        ?.scrollIntoView({
          // behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
    });
  }, [cursor, autoScroll]);

  // highlight words as video playing
  useEffect(() => {
    if (
      !transcriptElements ||
      (videoCreator.isPlaying &&
        videoCreator.time < lastTimeUpdated &&
        lastTimeUpdated < videoCreator.time + 5) ||
      Math.abs(lastTimeUpdated - videoCreator.time) < 0.1
    )
      return;
    setLastTimeUpdated(videoCreator.time);

    const newCursor = getCursorPosition();
    setCursor(newCursor);
  }, [transcriptElements, videoCreator.time, getCursorPosition]);

  // reset cursor on story or video change
  useEffect(() => {
    setCursor({ from: 0, to: 0 });
  }, [videoCreator.story, videoCreator.currentVideo]);

  useEffect(() => {
    if (showHistory && showSubtitles) {
      videoCreator.setShowSubtitles(!showSubtitles);
    }
  }, [showHistory]);

  useEffect(() => {
    if (showSubtitles && showHistory) {
      setShowHistory(!showHistory);
    }
  }, [showSubtitles]);

  const handleReplace = () => {
    const { startIndex, endIndex } = getSelectedStartEndIndex(scrollRef) || {};
    if (startIndex === undefined || endIndex === undefined) return;
    setShowReplaceModal({ startIndex, endIndex });
  };

  const handleEditTiming = () => {
    const selection = getSelectedStartEndIndex(scrollRef);
    if (transcriptElements && selection) {
      const { startIndex, endIndex } = selection;
      // For now, using the selected indices directly
      // This will need to be updated with proper text element logic
      setShowEditTimingModal({
        startIndex: startIndex,
        endIndex: endIndex || startIndex,
      });
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.code) {
        case 'Backspace':
          handleTranscriptDelete(
            cursor,
            setCursor,
            setLastTimeUpdated,
            scrollRef,
            isInKaraokeView,
          );
          break;
        case 'ArrowRight':
        case 'ArrowLeft':
          setAutoScroll(true);
          handleCursorMove(e.code === 'ArrowRight' ? 1 : -1, cursor, setCursor);
          break;
        case 'Space':
          e.preventDefault();
          if (isInKaraokeView) {
            handleAddPunctuation(e.code, cursor, setCursor);
          } else {
            videoCreator.isPlaying
              ? videoCreator.renderer?.pause()
              : videoCreator.refreshSourceAndPlay();
          }
          break;
        case 'Comma':
        case 'Period':
          handleAddPunctuation(e.code, cursor, setCursor);
          break;
        case 'Enter':
          if (isInKaraokeView) {
            handleAddKaraokeBreak(cursor);
          } else {
            handleAddPunctuation(e.code, cursor, setCursor);
          }
          break;
        case 'Backslash':
          if (e.shiftKey) {
            handleAddKaraokeBreak(cursor);
          }
          break;
      }
      e.stopImmediatePropagation();
      e.stopPropagation();
    },
    [
      handleTranscriptDelete,
      handleCursorMove,
      handleAddPunctuation,
      handleAddKaraokeBreak,
      isInKaraokeView,
      videoCreator,
      cursor,
      setCursor,
      setLastTimeUpdated,
      scrollRef,
    ],
  );

  // todo make sure keyboard events from other places are not intercepted
  useEffect(() => {
    if (showReplaceModal || photoSelection) return;
    const container = document.getElementById('transcript-container');
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      return function cleanup() {
        container.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [handleKeyDown, showReplaceModal, photoSelection]);

  const filterActionsInSubtitles = useCallback(
    (actions: TranscriptContextMenuActions[]) =>
      actions.filter(action => ['replace', 'removeText'].includes(action)),
    [],
  );

  const showOptionsForWordWithIndexAtPosition = useCallback(
    (
      index: number,
      positionXY: { x: number; y: number },
      selection: Selection | null,
    ) => {
      let actions: TranscriptContextMenuActions[] = getActions(
        index,
        selection,
        scrollRef,
      );
      if (!actions.length) return;
      if (isInKaraokeView && isSubtitlesTranslated) {
        actions = filterActionsInSubtitles(actions);
      }
      setSelectedWordIndex(index);
      setCursor({ from: index, to: index + 1 });
      setContextMenuPosition(positionXY);
      setContextMenuActions(actions);
    },
    [
      getActions,
      isInKaraokeView,
      isSubtitlesTranslated,
      filterActionsInSubtitles,
    ],
  );

  const mouseEnter = () => {
    scrollRef.current?.focus();
  };

  const mouseLeave = () => {
    scrollRef.current?.blur();
  };

  const showOptionsForSelection = useCallback(
    (
      e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
      selection: Selection,
      indices: { startIndex: number; endIndex: number },
    ) => {
      const { startIndex, endIndex } = indices;
      if (endIndex !== undefined && endIndex > -1) {
        showOptionsForWordWithIndexAtPosition(
          endIndex,
          { x: e.pageX, y: e.pageY },
          selection,
        );
      } else if (startIndex !== undefined && startIndex > -1) {
        showOptionsForWordWithIndexAtPosition(
          startIndex,
          { x: e.pageX, y: e.pageY },
          selection,
        );
      }
      if (
        endIndex &&
        endIndex > -1 &&
        endIndex < transcriptElements!.length &&
        transcriptElements![endIndex].state !== 'removed' &&
        transcriptElements![endIndex].state !== 'cut'
      ) {
        // do not set cursor on removed
        // videoCreator.setTime(transcriptElements![endIndex]?.ts || 0);
        setCursor({ from: endIndex, to: endIndex + 1 });
      }
    },
    [showOptionsForWordWithIndexAtPosition, transcriptElements],
  );

  const handleMouseUp: React.MouseEventHandler<HTMLDivElement> = useCallback(
    e => {
      const fn = async () => {
        if (e.button !== 0) return;
        // hack to wait for selection to be reset properly, otherwise onMouseUp old selection is possibly captured
        await new Promise(resolve => setTimeout(resolve, 200));
        const selection = window.getSelection();
        if (!(selection && selection.toString().length)) return;
        const indices = getBoundaryIndexesFromSelection(selection, scrollRef);
        if (!indices) return;
        showOptionsForSelection(e, selection, indices);
      };
      fn();
    },
    [getBoundaryIndexesFromSelection, showOptionsForSelection, scrollRef],
  );

  const handleRemoveText = async () => {
    handleTranscriptDelete(
      cursor,
      setCursor,
      setLastTimeUpdated,
      scrollRef,
      isInKaraokeView,
    );
  };

  const handleOpenPhotoModal = () => {
    const selection = window.getSelection();
    if (selection) {
      const boundaries = getBoundaryIndexesFromSelection(selection, scrollRef);
      const text = selection.toString();
      togglePhotoSelectionModal({ loc: boundaries!, text });
    }
  };

  const handleAddPhotoWrapper = async () => {
    if (!photoSelection || !selectedImage?.url) {
      togglePhotoSelectionModal(null);
      return;
    }
    togglePhotoSelectionModal(null);
    await handleAddPhoto(photoSelection, selectedImage);
  };

  const contextMenuCallback = async (
    action: TranscriptContextMenuActions | 'close',
  ) => {
    if (action === 'replace') {
      handleReplace();
    } else if (action === 'hideKaraoke') {
      handleHideKaraoke(scrollRef);
    } else if (action === 'removeText') {
      await handleRemoveText();
    } else if (action === 'removePhoto') {
      await handleRemovePhoto();
    } else if (action === 'saveAsClip') {
      await handleSaveAsClip(scrollRef);
    } else if (action === 'addPhoto') {
      handleOpenPhotoModal();
    } else if (action === 'restore' || action === 'showKaraoke') {
      await handleTranscriptRestore(action, scrollRef, setCursor);
    } else if (action === 'cutText') {
      await handleCutText(
        cursor,
        setCursor,
        setLastTimeUpdated,
        scrollRef,
        isInKaraokeView,
      );
    } else if (action === 'pasteTextAfter' || action === 'pasteTextBefore') {
      handlePasteText(action === 'pasteTextAfter', scrollRef);
    } else if (action === 'editTiming') {
      handleEditTiming();
    }
    if (action !== 'replace') {
      setSelectedWordIndex(-1);
    }
    setContextMenuPosition(null);
  };

  const handleContextMenu: React.MouseEventHandler<HTMLDivElement> =
    useCallback(
      e => {
        e.preventDefault();
        const selection = window.getSelection();
        if (selection && selection.type === 'Range') {
          const indices = getBoundaryIndexesFromSelection(selection, scrollRef);
          if (indices) showOptionsForSelection(e, selection, indices);
          return;
        }
        let index = Number((e.target as HTMLElement).dataset.index);
        if (index != null && !isNaN(index)) {
          showOptionsForWordWithIndexAtPosition(
            index,
            { x: e.pageX, y: e.pageY },
            selection,
          );
        }
      },
      [
        getBoundaryIndexesFromSelection,
        showOptionsForSelection,
        showOptionsForWordWithIndexAtPosition,
        scrollRef,
      ],
    );

  const longPressDuration = 600;
  const touchTolerance = 10;
  const touchData = React.useRef<{
    timerId: ReturnType<typeof setTimeout> | null;
    startX: number;
    startY: number;
  }>({
    timerId: null,
    startX: 0,
    startY: 0,
  });

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    touchData.current.startX = touch.pageX;
    touchData.current.startY = touch.pageY;
    touchData.current.timerId = setTimeout(() => {
      touchData.current.timerId = null;

      // Prevent native menu
      e.preventDefault();

      // Simulate context menu triggering
      const fakeEvent = {
        preventDefault: () => {},
        target: document.elementFromPoint(
          touch.pageX - window.pageXOffset,
          touch.pageY - window.pageYOffset,
        ),
        pageX: touch.pageX,
        pageY: touch.pageY,
      } as unknown as React.MouseEvent<HTMLDivElement>;
      handleContextMenu(fakeEvent);
    }, longPressDuration);
  };

  const onTouchEnd = () => {
    if (touchData.current.timerId) {
      clearTimeout(touchData.current.timerId);
      touchData.current.timerId = null;
    }
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    setAutoScroll(false);
    if (!touchData.current.timerId) return;
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.pageX - touchData.current.startX);
    const deltaY = Math.abs(touch.pageY - touchData.current.startY);
    if (deltaX > touchTolerance || deltaY > touchTolerance) {
      clearTimeout(touchData.current.timerId);
      touchData.current.timerId = null;
    }
  };

  return (
    <Main
      id={'transcript-container'}
      ref={scrollRef}
      tabIndex={-1}
      onWheel={() => {
        setAutoScroll(false);
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClick={e => {
        const index = Number((e.target as HTMLElement).dataset.index);
        if (!isNaN(index)) {
          skipToWordWithIndex(index, setCursor, setAutoScroll);
        }
      }}
      onContextMenu={handleContextMenu}
      onMouseUp={handleMouseUp}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
    >
      {contextMenuPosition && contextMenuActions && selectedWordIndex > -1 && (
        <TranscriptContextMenu
          positionXY={contextMenuPosition}
          actions={contextMenuActions}
          actionCallback={contextMenuCallback}
          clientHeight={scrollRef.current?.clientHeight!}
          overridenActionTitles={
            isInKaraokeView && isSubtitlesTranslated
              ? {
                  removeText: 'Remove Karaoke',
                  removeTextLoading: 'Remove Karaoke',
                }
              : undefined
          }
        />
      )}
      {showReplaceModal && (
        <WordReplacementModal
          selection={showReplaceModal}
          transcriptElements={transcriptElements || []}
          discard={() => {
            setShowReplaceModal(null);
            setSelectedWordIndex(-1);
          }}
          onSubmit={handleTranscriptTextReplace}
        />
      )}
      <EditTimingSection
        transcriptElements={transcriptElements || []}
        isSubtitlesTranslated={isSubtitlesTranslated}
        showEditTimingModal={showEditTimingModal?.startIndex !== undefined}
        sourceVideoFramerate={videoCreator.getSourceVideoFrameRate()!}
        startIndex={showEditTimingModal?.startIndex || 0}
        endIndex={
          showEditTimingModal?.endIndex || showEditTimingModal?.startIndex || 0
        }
        discard={() => setShowEditTimingModal(null)}
      />
      {showReplaceWarning && !showSubtitles && (
        <RegenerateTranscriptionModal
          type={'transcription'}
          confirm={async () => {
            transcriptionStore.regenerateTranscription();
            setShowReplaceWarning(false);
          }}
          discard={() => {
            setShowReplaceWarning(false);
          }}
        />
      )}
      {showReplaceWarning && showSubtitles && (
        <RegenerateTranscriptionModal
          type={
            originalTranscription!.language.includes('en')
              ? 'karaoke breaks'
              : 'subtitles'
          }
          confirm={async () => {
            if (originalTranscription!.language.includes('en')) {
              if (useNewKaraoke) {
                commandProduceKaraoke({ replaceTrack: true });
              } else if (process.env.REACT_APP_NEW_KARAOKE == 'true') {
                videoCreator.karaokeProducer.produceKaraoke();
              } else {
                videoCreator.karaokeProducer.createKaraokeBreaksFromExistingElements();
              }
            } else {
              videoCreator.refetchSubtitlesForCurrentVideo();
            }
            setShowReplaceWarning(false);
          }}
          discard={() => {
            setShowReplaceWarning(false);
          }}
        />
      )}

      <Header>
        <Heading>Transcript</Heading>
        <Buttons>
          <LeftSideButtons>
            {enableFixTranscriptButton && (
              <>
                <PanelButton
                  tabIndex={-1}
                  onFocus={() => scrollRef.current!.focus()}
                  onClick={() => videoCreator.fixTranscription()}
                  data-tooltip-id="repair-tooltip"
                  data-tooltip-content="Click if your transcript is misaligned"
                >
                  <RepairIcon fillColor="#A9A9A9" />
                </PanelButton>
                <Tooltip
                  id="repair-tooltip"
                  style={{
                    padding: '8px',
                    fontSize: '12px',
                    lineHeight: '120%',
                    zIndex: '10',
                    fontWeight: 600,
                  }}
                  place="top"
                  positionStrategy="fixed"
                />
              </>
            )}
            <CopyTranscriptIconButton />
          </LeftSideButtons>
          <RightSideButtons>
            {originalTranscription &&
              (useNewKaraoke
                ? isKaraokeGenerated
                : videoCreator.karaokeProducer.hasElements()) && (
                <>
                  {showSubtitles && (
                    <PanelButton
                      tabIndex={-1}
                      onFocus={() => scrollRef.current!.focus()}
                      onClick={() => setShowReplaceWarning(true)}
                    >
                      <ReplaceIcon strokeColor="#A9A9A9" />
                    </PanelButton>
                  )}
                  <div style={{ height: '20px' }}>
                    <PanelButton
                      tabIndex={-1}
                      outlined={true}
                      isPressed={showSubtitles}
                      onFocus={() => scrollRef.current!.focus()}
                      onClick={() => {
                        analytics.track('transcript_toggle_karaoke', {
                          storyId: videoCreator.story?.id,
                          storyTitle: videoCreator.story?.title,
                          videoId: videoCreator.currentVideo?.id,
                          videoTitle: videoCreator.currentVideo?.title,
                        });
                        if (!showSubtitles) {
                          videoCreator.sidebarOptions = SidebarOption.karaoke;
                        }
                        videoCreator.setShowSubtitles(!showSubtitles);
                      }}
                    >
                      <SubtitlesButton>KARAOKE</SubtitlesButton>
                    </PanelButton>
                  </div>
                </>
              )}
            {inDebugMode() && (
              <PanelButton
                tabIndex={-1}
                onFocus={() => scrollRef.current!.focus()}
                onClick={() => setDebugEnabled(true)}
              >
                <SubtitlesButton>DEBUG</SubtitlesButton>
              </PanelButton>
            )}
            <PanelButton
              tabIndex={-1}
              onFocus={() => scrollRef.current!.focus()}
              isPressed={showHistory}
              onClick={() =>
                startTransition(() => {
                  analytics.track('transcript_toggle_redline', {
                    storyId: videoCreator.story?.id,
                    storyTitle: videoCreator.story?.title,
                    videoId: videoCreator.currentVideo?.id,
                    videoTitle: videoCreator.currentVideo?.title,
                  });
                  setShowHistory(cur => !cur);
                })
              }
            >
              <TranscriptHistoryIcon
                strokeColor={showHistory ? '#030419' : '#A9A9A9'}
              />
            </PanelButton>
          </RightSideButtons>
        </Buttons>
      </Header>
      <ScrollableText
        id="transcript-scrollable-container"
        data-testid="transcript-scrollable-container"
      >
        <TranscriptContent
          videoCreator={videoCreator}
          transcriptionUrl={transcriptionStore?.transcriptionUrl || ''}
          transcriptionLoadingStatus={
            transcriptionStore?.transcriptionLoadingStatus ||
            TranscriptionStatus.None
          }
          isEmpty={!transcriptElements?.length}
          showSubtitles={showSubtitles}
          isSubtitlesTranslated={isSubtitlesTranslated}
          isInKaraokeView={isInKaraokeView}
          enableNewKaraokeArchitecture={!!useNewKaraoke}
          karaokeLines={karaokeLines}
          showHistory={showHistory}
          cursor={cursor}
          transcriptBlocks={transcriptBlocks}
          transcriptElements={transcriptElements}
          scrollRef={scrollRef}
          setShowReplaceWarning={setShowReplaceWarning}
        />
      </ScrollableText>
      {photoSelection && (
        <Modal isOpen={true} closeModal={() => togglePhotoSelectionModal(null)}>
          <ModalWrapper>
            <PhotoModal
              TopComponent={props => (
                <PhotoModalTop
                  {...props}
                  addImageLabel="Add Image"
                  addImageLoadingLabel="Adding..."
                  replaceAction={handleAddPhotoWrapper}
                  title="Swap or search for a new image"
                  isSelected={!!selectedImage}
                  origin="transcript"
                  selectedImage={selectedImage}
                  isLoading={false}
                  setSelectedImage={setSelectedImage}
                />
              )}
              showDescription
              otherFields={['stock', 'ai', 'org_photos']}
              onCloseSelf={() => togglePhotoSelectionModal(null)}
              openPrevModal={() => {}}
              selectedImageUrl={selectedImage?.url || null}
              setSelectedImage={setSelectedImage}
              origin="transcript"
              searchBarRadius="10px"
            />
          </ModalWrapper>
        </Modal>
      )}
      {debugEnabled && (
        <DebugModal
          title="Transcript Debug"
          content={transcriptElements}
          onClose={() => {
            setDebugEnabled(false);
          }}
        />
      )}
    </Main>
  );
});

const TranscriptContent = React.memo(
  ({
    videoCreator,
    transcriptionUrl,
    transcriptionLoadingStatus,
    isEmpty,
    showSubtitles,
    isSubtitlesTranslated,
    isInKaraokeView,
    enableNewKaraokeArchitecture,
    showHistory,
    cursor,
    transcriptBlocks,
    transcriptElements,
    karaokeLines,
    scrollRef,
    setShowReplaceWarning,
  }: {
    videoCreator: IVideoCreatorStore;
    transcriptionUrl?: string;
    transcriptionLoadingStatus: TranscriptionStatus;
    isEmpty: boolean;
    showSubtitles: boolean;
    isSubtitlesTranslated: boolean;
    isInKaraokeView: boolean;
    enableNewKaraokeArchitecture: boolean;
    showHistory: boolean;
    cursor: { from: number; to: number };
    transcriptBlocks: TranscriptElementBlock[];
    transcriptElements: TranscriptElement[] | undefined;
    karaokeLines: KaraokeLine[] | undefined;
    scrollRef: React.RefObject<HTMLDivElement>;
    setShowReplaceWarning: (show: boolean) => void;
  }) => {
    if (
      videoCreator.storyId &&
      !transcriptionUrl &&
      transcriptionLoadingStatus !== TranscriptionStatus.Failed &&
      transcriptionLoadingStatus !== TranscriptionStatus.GenerationFailed
    ) {
      return (
        <div>
          <div style={{ width: '100%' }}>Transcript is regenerating...</div>
          {transcriptionLoadingStatus === 'none' && (
            <div>
              Click{' '}
              <PanelButton onClick={() => setShowReplaceWarning(true)}>
                <ReplaceIcon />
              </PanelButton>{' '}
              to Re-generate again
            </div>
          )}
        </div>
      );
    }

    if (
      !transcriptionUrl &&
      (transcriptionLoadingStatus === TranscriptionStatus.Failed ||
        transcriptionLoadingStatus === TranscriptionStatus.GenerationFailed)
    ) {
      return (
        <div>
          <div style={{ width: '100%' }}>
            Failed to generate the transcript.
          </div>
          <div>
            Click{' '}
            <PanelButton onClick={() => setShowReplaceWarning(true)}>
              <ReplaceIcon />
            </PanelButton>{' '}
            to try again
          </div>
          <div>Or contact developers about the issue</div>
        </div>
      );
    }
    if (
      transcriptionUrl &&
      transcriptionLoadingStatus === TranscriptionStatus.Loading
    ) {
      return <div>Transcription is loading...</div>;
    }
    if (
      transcriptionUrl &&
      transcriptionLoadingStatus === TranscriptionStatus.Failed
    ) {
      return <div>Transcription loading failed. Try refreshing the page.</div>;
    }
    if (transcriptionLoadingStatus === TranscriptionStatus.Failed && isEmpty) {
      return <div>Transcription loading failed. Try refreshing the page.</div>;
    }
    if (showSubtitles && isSubtitlesTranslated) {
      return <KaraokeBreaksSubtitlesView cursor={cursor} />;
    } else if (
      isInKaraokeView &&
      enableNewKaraokeArchitecture &&
      karaokeLines
    ) {
      return <KaraokeLinesView cursor={cursor} karaokeLines={karaokeLines} />;
    } else if (isInKaraokeView && !enableNewKaraokeArchitecture) {
      return <KaraokeBreaksView cursor={cursor} />;
    } else if (showHistory) {
      return <TranscriptionDiffNew cursor={cursor} blocks={transcriptBlocks} />;
    } else {
      return (
        <TranscriptionText
          cursor={cursor}
          transcriptElements={transcriptElements}
          scrollRef={scrollRef}
        />
      );
    }
  },
);

const Main = styled.div`
  height: calc(100% - 16px);
  box-sizing: border-box;
  width: 100%;
  font-size: 14px;
  margin-left: 32px;
  overflow: hidden;
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  width: 100%;
  &:focus {
    outline: none;
  }

  ${forIPad(`
    margin-left: 8px;
  `)}
`;

const ScrollableText = styled.div.withConfig({
  displayName: 'ScrollableText',
})`
  overflow-y: scroll;
  height: 100%;
  width: 100%;
  padding-right: 14px;
  padding-bottom: 42px;
  box-sizing: border-box;
  line-height: 175%;
  margin-top: 12px;
  float: left;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Heading = styled.span`
  color: #f2d093;
  font-weight: 700;
  line-height: normal;
  margin: 4px 0;
`;

const Buttons = styled.div`
  height: 26px;
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: space-between;
  padding-left: 6px;
  padding-right: 6px;
`;

const PanelButton = styled.button.attrs(
  (props: { isPressed: boolean; outlined: boolean }) => props,
)`
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background-color: ${props => (props.isPressed ? '#F2D093' : 'transparent')};
  color: ${props => (props.isPressed ? '#030419' : '#A9A9A9')};
  border: ${props =>
    props.outlined
      ? `1px solid ${props.isPressed ? '#F2D093' : '#A9A9A9'}`
      : 'none'};
  height: 100%;
  display: flex;
  align-items: center;

  &:hover {
    transform: scale(1.05);
  }
`;

const SubtitlesButton = styled.span`
  font-size: 10px;
`;

const RightSideButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;
`;

const LeftSideButtons = styled(RightSideButtons)`
  gap: 2px;
`;

const ModalWrapper = styled.div`
  background-color: #030419;
  color: #f3e9d7;
  width: 500px;
  box-shadow: 8px 8px 16px 0px rgba(0, 0, 0, 0.4);
  border-radius: 10px;
  padding: 20px 0;
  border: 1px solid #484848;
`;
